import React, { useState } from 'react';
import {
  salesApi, expensesApi, complaintsApi, inventoryApi,
  customersApi, enquiriesApi, servicesApi, attendanceApi, staffApi,
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Download, Share2, BarChart2, Package, Users, AlertCircle, Calendar } from '../components/Icons';
import { Spin } from '../components/UI';

const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const daysAgo  = n => { const d = new Date(Date.now() - n * 86400000); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
const fmt      = n  => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtDate  = d  => new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { dateStyle: 'medium' });
const pct      = (a, b) => b > 0 ? ((a / b) * 100).toFixed(1) + '%' : '—';

const CSS = `
  @page { size: A4 landscape; margin: 12mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; font-size: 11px; color: #111; }
  h1 { font-size: 16px; color: #1e3a5f; }
  h2 { font-size: 12px; color: #1e3a5f; margin: 10px 0 6px; padding-bottom: 4px; border-bottom: 2px solid #1e3a5f; }
  .hd { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #1e3a5f; padding-bottom:10px; margin-bottom:14px; }
  .hd-r { text-align:right; font-size:11px; color:#555; }
  table { width:100%; border-collapse:collapse; font-size:10.5px; margin-bottom:12px; }
  thead tr { background:#1e3a5f; color:#fff; }
  th { padding:5px 6px; text-align:left; font-weight:600; }
  td { padding:4px 6px; border-bottom:1px solid #e5e7eb; }
  tr:last-child td { border-bottom:none; }
  .sumgrid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:14px; }
  .sumbox { border-radius:6px; padding:10px 14px; }
  .sumlbl { font-size:10px; color:#666; text-transform:uppercase; font-weight:600; margin-bottom:3px; }
  .sumval { font-size:16px; font-weight:700; }
  .foot { margin-top:14px; font-size:9px; color:#aaa; text-align:center; border-top:1px solid #e5e7eb; padding-top:8px; }
`;

const HDR = (title, period) => `
  <div class="hd">
    <div>
      <h1>Sakthi Solar Solutions</h1>
      <div style="font-size:11px;color:#555;margin-top:3px">
        Sakthi Arcade, Market Jn, Kandiyoor, Mavelikara - 690103<br/>
        Ph: 9446191276, 9846065984 &nbsp;|&nbsp; info@sakthiinverters.com
      </div>
    </div>
    <div class="hd-r">
      <div style="font-size:13px;font-weight:700;color:#1e3a5f">${title}</div>
      <div>${period}</div>
      <div style="color:#aaa;font-size:10px">Generated: ${new Date().toLocaleDateString('en-IN',{dateStyle:'medium'})}</div>
    </div>
  </div>
`;

const FTR = `<div class="foot">Sakthi Solar Solutions &nbsp;|&nbsp; Computer-generated report. &nbsp;|&nbsp; www.sakthiinverters.com<br/><span style="font-size:9px;color:#ccc">Developed by Data Systems Intelligence Pvt Ltd</span></div>`;

const openPDF = body => {
  const w = window.open('', '_blank', 'width=1100,height=800');
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><style>${CSS}</style></head><body>${body}${FTR}</body></html>`);
  w.document.close();
  w.onload = () => { w.focus(); w.print(); };
};

const saveCSV = (name, csv) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

const DateRange = ({ from, to, setFrom, setTo, minDate, onGo, loading, label }) => (
  <div className="card" style={{ marginBottom: 16 }}>
    <div className="card-hd"><div className="card-title">{label}</div></div>
    <div className="card-body">
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>From Date</label>
          <input type="date" value={from} min={minDate} max={to || todayStr()} onChange={e => setFrom(e.target.value)}/>
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>To Date</label>
          <input type="date" value={to} min={from} max={todayStr()} onChange={e => setTo(e.target.value)}/>
        </div>
        <button className="btn btn-primary" onClick={onGo} disabled={loading} style={{ whiteSpace: 'nowrap' }}>
          {loading ? 'Generating…' : 'Generate Report'}
        </button>
      </div>
    </div>
  </div>
);

// ─── Financial ────────────────────────────────────────────────────────────────

function FinancialReport({ isSA }) {
  const minDate = isSA ? '2020-01-01' : daysAgo(7);
  const [from, setFrom]         = useState(todayStr());
  const [to,   setTo]           = useState(todayStr());
  const [sales, setSales]       = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [generated, setGen]     = useState(false);
  const [err, setErr]           = useState('');

  const generate = async () => {
    if (from > to) { setErr('From date cannot be after To date.'); return; }
    if (!isSA && from < minDate) { setErr(`Admin reports are limited to the last 7 days (from ${fmtDate(minDate)}).`); return; }
    setErr(''); setLoading(true);
    try {
      const [s, e, c] = await Promise.all([
        salesApi.list({ page_size: 2000 }),
        expensesApi.list({ page_size: 2000 }),
        complaintsApi.list({ page_size: 2000 }),
      ]);
      const inRange = ds => ds >= from && ds <= to;
      setSales((s.data || []).filter(x => x.created_at.slice(0,10) >= from && x.created_at.slice(0,10) <= to));
      setExpenses((e.data || []).filter(x => inRange(x.expense_date)));
      setComplaints((c.data || []).filter(x =>
        Number(x.amount_received) > 0 && x.payment_recorded_at &&
        inRange(x.payment_recorded_at.slice(0,10))
      ));
      setGen(true);
    } catch { setErr('Failed to load report data.'); }
    finally { setLoading(false); }
  };

  const complaintIncome = complaints.reduce((s, x) => s + Number(x.amount_received || 0), 0);
  const revenue    = sales.reduce((s, x) => {
    if (x.payment_status === 'paid')    return s + Number(x.total);
    if (x.payment_status === 'partial') return s + Number(x.amount_paid || 0);
    return s;
  }, complaintIncome);
  const pendingRev = sales.reduce((s, x) => {
    if (x.payment_status === 'paid')    return s;
    if (x.payment_status === 'partial') return s + (Number(x.total) - Number(x.amount_paid || 0));
    return s + Number(x.total);
  }, 0);
  const totalExp = expenses.reduce((s, x) => s + Number(x.amount), 0);
  const net      = revenue - totalExp;

  const doCSV = () => {
    const header  = `Sakthi Solar Solutions - Financial Report\nPeriod: ${fmtDate(from)} to ${fmtDate(to)}\n\n`;
    const incHead = 'INCOME\nDate,Type,Payment Mode,Status,Amount (INR)\n';
    const incRows = [
      ...sales.map(s => `${s.created_at.slice(0,10)},Sale,${s.payment_mode || ''},${s.payment_status},${s.payment_status === 'paid' ? s.total : (s.amount_paid || 0)}`),
      ...complaints.map(c => `${(c.updated_at || c.created_at || '').slice(0,10)},Service,${c.payment_mode || ''},paid,${c.amount_received}`),
    ].join('\n');
    const expHead = '\n\nEXPENSES\nDate,Category,Description,Amount (INR)\n';
    const expRows = expenses.map(e => `${e.expense_date},${e.category},"${e.title}",${e.amount}`).join('\n');
    const totals  = `\n\nSUMMARY\nRevenue (Paid),${revenue}\nPending Revenue,${pendingRev}\nTotal Expenses,${totalExp}\nNet Income,${net}`;
    saveCSV(`sakthi_solar_financial_${from}_to_${to}.csv`, header + incHead + incRows + expHead + expRows + totals);
  };

  const doPDF = () => {
    const incRows = [
      ...sales.map((s, i) => {
        const amt = s.payment_status === 'paid' ? Number(s.total) : s.payment_status === 'partial' ? Number(s.amount_paid || 0) : 0;
        return `<tr><td>${i+1}</td><td>${s.created_at.slice(0,10)}</td><td>${s.customer?.full_name || '—'}</td><td>Sale</td><td>${s.payment_mode || '—'}</td><td style="text-align:right;color:${s.payment_status==='paid'?'green':'#b45309'};font-weight:600">&#8377;${amt.toLocaleString('en-IN',{maximumFractionDigits:2})}</td><td style="text-align:center"><span style="font-size:10px;padding:2px 6px;border-radius:4px;background:${s.payment_status==='paid'?'#dcfce7':s.payment_status==='partial'?'#fef3c7':'#fee2e2'};color:${s.payment_status==='paid'?'green':s.payment_status==='partial'?'#b45309':'red'}">${s.payment_status}</span></td></tr>`;
      }),
      ...complaints.map((c, i) => `<tr><td>${sales.length+i+1}</td><td>${(c.updated_at||c.created_at||'').slice(0,10)}</td><td>${c.customer?.full_name||'—'}</td><td>Service</td><td>${c.payment_mode||'—'}</td><td style="text-align:right;color:green;font-weight:600">&#8377;${Number(c.amount_received).toLocaleString('en-IN',{maximumFractionDigits:2})}</td><td style="text-align:center"><span style="font-size:10px;padding:2px 6px;border-radius:4px;background:#dcfce7;color:green">paid</span></td></tr>`),
    ].join('');
    const expRows = expenses.map((e, i) => `<tr><td>${i+1}</td><td>${e.expense_date}</td><td>${e.category}</td><td>${e.title}</td><td style="text-align:right;color:red;font-weight:600">&#8377;${Number(e.amount).toLocaleString('en-IN',{maximumFractionDigits:2})}</td></tr>`).join('');
    const expCatMap = {};
    expenses.forEach(e => { expCatMap[e.category] = (expCatMap[e.category]||0) + Number(e.amount); });
    const expCatRows = Object.entries(expCatMap).map(([cat, amt]) => `<tr><td style="color:#555">${cat}</td><td style="text-align:right;font-weight:600">&#8377;${amt.toLocaleString('en-IN',{maximumFractionDigits:2})}</td></tr>`).join('');

    const body = `
      ${HDR('FINANCIAL REPORT', `Period: ${fmtDate(from)} — ${fmtDate(to)}`)}
      <div style="background:#f0f4ff;border-left:4px solid #1e3a5f;border-radius:4px;padding:10px 14px;margin-bottom:14px;font-size:11px;color:#1e3a5f;line-height:1.8">
        <strong>Summary</strong> — ${sales.length} sale${sales.length!==1?'s':''}, ${expenses.length} expense entr${expenses.length!==1?'ies':'y'} &nbsp;|&nbsp;
        Revenue: <strong>&#8377;${revenue.toLocaleString('en-IN',{maximumFractionDigits:2})}</strong> &nbsp;|&nbsp;
        Expenses: <strong>&#8377;${totalExp.toLocaleString('en-IN',{maximumFractionDigits:2})}</strong> &nbsp;|&nbsp;
        Net: <strong style="color:${net>=0?'green':'red'}">&#8377;${net.toLocaleString('en-IN',{maximumFractionDigits:2})}</strong>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:14px">
        <div>
          <h2>Revenue / Income (${sales.length + complaints.length} entries)</h2>
          <table><thead><tr><th>#</th><th>Date</th><th>Customer</th><th>Type</th><th>Mode</th><th style="text-align:right">Amount</th><th style="text-align:center">Status</th></tr></thead>
          <tbody>${incRows||'<tr><td colspan="7" style="color:#aaa;text-align:center;padding:10px">No income in this period</td></tr>'}</tbody></table>
        </div>
        <div>
          <h2>Expenses (${expenses.length} entries)</h2>
          <table><thead><tr><th>#</th><th>Date</th><th>Category</th><th>Description</th><th style="text-align:right">Amount</th></tr></thead>
          <tbody>${expRows||'<tr><td colspan="5" style="color:#aaa;text-align:center;padding:10px">No expenses in this period</td></tr>'}</tbody></table>
          ${Object.keys(expCatMap).length > 1 ? `<table style="width:auto;margin-left:auto"><thead><tr style="background:#f5f7fa"><th colspan="2" style="color:#1e3a5f;font-size:10px">By Category</th></tr></thead><tbody>${expCatRows}</tbody></table>` : ''}
        </div>
      </div>
      <div class="sumgrid">
        <div class="sumbox" style="background:#f0fdf4;border:1px solid #bbf7d0"><div class="sumlbl">Revenue (Paid)</div><div class="sumval" style="color:green">&#8377;${revenue.toLocaleString('en-IN',{maximumFractionDigits:2})}</div><div style="font-size:9px;color:#555;margin-top:2px">${sales.filter(s=>s.payment_status==='paid').length} paid sales</div></div>
        <div class="sumbox" style="background:#fffbeb;border:1px solid #fde68a"><div class="sumlbl">Pending / Partial</div><div class="sumval" style="color:#b45309">&#8377;${pendingRev.toLocaleString('en-IN',{maximumFractionDigits:2})}</div><div style="font-size:9px;color:#555;margin-top:2px">${sales.filter(s=>s.payment_status!=='paid').length} pending sales</div></div>
        <div class="sumbox" style="background:#fef2f2;border:1px solid #fecaca"><div class="sumlbl">Total Expenses</div><div class="sumval" style="color:red">&#8377;${totalExp.toLocaleString('en-IN',{maximumFractionDigits:2})}</div><div style="font-size:9px;color:#555;margin-top:2px">${expenses.length} entries</div></div>
        <div class="sumbox" style="background:${net>=0?'#f0fdf4':'#fef2f2'};border:1px solid ${net>=0?'#bbf7d0':'#fecaca'}"><div class="sumlbl">Net Income</div><div class="sumval" style="color:${net>=0?'green':'red'}">&#8377;${net.toLocaleString('en-IN',{maximumFractionDigits:2})}</div></div>
      </div>
    `;
    openPDF(body);
  };

  const sendWA = () => {
    const lines = [
      `*Sakthi Solar Solutions - Report*`,
      `Period: ${fmtDate(from)} to ${fmtDate(to)}`,
      ``,
      `Revenue (Paid): ${fmt(revenue)}`,
      `Pending Revenue: ${fmt(pendingRev)}`,
      `Expenses: ${fmt(totalExp)}`,
      `Net Income: ${fmt(net)}`,
      ``,
      `Sales: ${sales.length} | Expenses: ${expenses.length}`,
    ];
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join('\n'))}`, '_blank');
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-hd"><div className="card-title"><BarChart2 s={15}/>Generate Financial Report</div></div>
        <div className="card-body">
          {!isSA && <div className="notice n-info" style={{ marginBottom: 14 }}>Admin reports are limited to the last 7 days (from {fmtDate(minDate)}).</div>}
          {err && <div className="notice n-err" style={{ marginBottom: 14 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>From Date</label>
              <input type="date" value={from} min={minDate} max={to || todayStr()} onChange={e => setFrom(e.target.value)}/>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>To Date</label>
              <input type="date" value={to} min={from} max={todayStr()} onChange={e => setTo(e.target.value)}/>
            </div>
            <button className="btn btn-primary" onClick={generate} disabled={loading} style={{ whiteSpace: 'nowrap' }}>
              {loading ? 'Generating…' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {loading && <Spin label="Loading report data…"/>}

      {generated && !loading && (
        <>
          <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--navy)', background: '#f0f4ff' }}>
            <div className="card-body">
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy)', marginBottom: 6 }}>
                Financial Report — {fmtDate(from)} to {fmtDate(to)}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.8 }}>
                {sales.length} sale{sales.length !== 1 ? 's' : ''} &nbsp;|&nbsp;
                {expenses.length} expense entr{expenses.length !== 1 ? 'ies' : 'y'} &nbsp;|&nbsp;
                Revenue: <strong style={{ color: 'var(--green)' }}>{fmt(revenue)}</strong> &nbsp;|&nbsp;
                Pending: <strong style={{ color: 'var(--amber)' }}>{fmt(pendingRev)}</strong> &nbsp;|&nbsp;
                Expenses: <strong style={{ color: 'var(--red)' }}>{fmt(totalExp)}</strong> &nbsp;|&nbsp;
                Net: <strong style={{ color: net >= 0 ? 'var(--green)' : 'var(--red)' }}>{fmt(net)}</strong>.
                {net >= 0 ? ' Business is profitable for this period.' : ' Expenses exceed income for this period.'}
              </div>
            </div>
          </div>

          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="stat-card" style={{ borderTop: '3px solid var(--green)' }}>
              <div className="stat-lbl">Revenue (Paid)</div>
              <div className="stat-val" style={{ color: 'var(--green)', fontSize: 22 }}>{fmt(revenue)}</div>
              <div className="stat-sub">{sales.filter(s => s.payment_status === 'paid').length} paid{complaints.length > 0 ? ` + ${complaints.length} svc` : ''}</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--amber)' }}>
              <div className="stat-lbl">Pending Revenue</div>
              <div className="stat-val" style={{ color: 'var(--amber)', fontSize: 22 }}>{fmt(pendingRev)}</div>
              <div className="stat-sub">{sales.filter(s => s.payment_status !== 'paid').length} pending sales</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--red)' }}>
              <div className="stat-lbl">Total Expenses</div>
              <div className="stat-val" style={{ color: 'var(--red)', fontSize: 22 }}>{fmt(totalExp)}</div>
              <div className="stat-sub">{expenses.length} entr{expenses.length !== 1 ? 'ies' : 'y'}</div>
            </div>
            <div className="stat-card" style={{ borderTop: `3px solid ${net >= 0 ? 'var(--green)' : 'var(--red)'}` }}>
              <div className="stat-lbl">Net Income</div>
              <div className="stat-val" style={{ color: net >= 0 ? 'var(--green)' : 'var(--red)', fontSize: 22 }}>{fmt(net)}</div>
              <div className="stat-sub">Revenue minus expenses</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={doPDF}><Download s={14}/> Download PDF</button>
            <button className="btn" onClick={doCSV}><Download s={14}/> Download CSV</button>
            <button className="btn" onClick={sendWA} style={{ color: '#25d366', borderColor: '#25d366' }}>
              <Share2 s={14}/> Share on WhatsApp
            </button>
          </div>

          {(sales.length > 0 || complaints.length > 0) && (
            <div className="card card-0" style={{ marginBottom: 16 }}>
              <div className="card-hd"><div className="card-title">Income — Sales &amp; Service ({sales.length + complaints.length})</div></div>
              <div className="tbl-wrap"><table>
                <thead><tr><th>Date</th><th>Customer</th><th>Type</th><th>Payment Mode</th><th>Status</th><th>Amount</th></tr></thead>
                <tbody>
                  {sales.map(s => {
                    const paidAmt = s.payment_status === 'paid' ? Number(s.total) : Number(s.amount_paid || 0);
                    const pendAmt = Number(s.total) - paidAmt;
                    return (
                      <tr key={s.id}>
                        <td style={{ fontSize: 12 }}>{s.created_at.slice(0,10)}</td>
                        <td>{s.customer?.full_name || '—'}</td>
                        <td><span className="badge b-info">Sale</span></td>
                        <td>{s.payment_mode || '—'}</td>
                        <td><span className={`badge ${s.payment_status === 'paid' ? 'b-ok' : s.payment_status === 'partial' ? 'b-warn' : 'b-err'}`}>{s.payment_status}</span></td>
                        <td style={{ fontWeight: 600, color: 'var(--green)' }}>
                          {fmt(paidAmt)}
                          {s.payment_status === 'partial' && <span style={{ fontSize: 11, color: 'var(--amber)', marginLeft: 6 }}>({fmt(pendAmt)} pending)</span>}
                        </td>
                      </tr>
                    );
                  })}
                  {complaints.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontSize: 12 }}>{(c.updated_at || c.created_at || '').slice(0,10)}</td>
                      <td>{c.customer?.full_name || c.walk_in_name || '—'}</td>
                      <td><span className="badge b-warn">Service</span></td>
                      <td>{c.payment_mode || '—'}</td>
                      <td><span className="badge b-ok">paid</span></td>
                      <td style={{ fontWeight: 600, color: 'var(--green)' }}>{fmt(c.amount_received)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}

          {expenses.length > 0 && (
            <div className="card card-0">
              <div className="card-hd"><div className="card-title">Expenses ({expenses.length})</div></div>
              <div className="tbl-wrap"><table>
                <thead><tr><th>Date</th><th>Category</th><th>Description</th><th>Amount</th></tr></thead>
                <tbody>
                  {expenses.map(e => (
                    <tr key={e.id}>
                      <td style={{ fontSize: 12 }}>{e.expense_date}</td>
                      <td>{e.category}</td>
                      <td>{e.title}</td>
                      <td style={{ fontWeight: 600, color: 'var(--red)' }}>{fmt(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}

          {sales.length === 0 && expenses.length === 0 && (
            <div className="card"><div className="card-body" style={{ color: 'var(--text2)', fontSize: 13 }}>
              No income or expense records found for the selected period.
            </div></div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Inventory & Stock ────────────────────────────────────────────────────────

function InventoryReport() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [generated, setGen]     = useState(false);
  const [err, setErr]           = useState('');

  const generate = async () => {
    setErr(''); setLoading(true);
    try {
      const { data } = await inventoryApi.list({ page_size: 2000 });
      setProducts(data || []);
      setGen(true);
    } catch { setErr('Failed to load inventory data.'); }
    finally { setLoading(false); }
  };

  const inStock   = products.filter(p => p.stock_qty > 0);
  const lowStock  = products.filter(p => p.stock_qty > 0 && p.stock_qty <= (p.low_stock_threshold || 5));
  const outStock  = products.filter(p => p.stock_qty === 0);
  const totalVal  = products.reduce((s, p) => s + Number(p.stock_qty || 0) * Number(p.mrp || 0), 0);

  const doCSV = () => {
    const lines = [
      'Sakthi Solar Solutions - Inventory & Stock Report',
      `Generated: ${new Date().toLocaleDateString('en-IN',{dateStyle:'medium'})}`,
      '',
      'Product,Category,Brand,SKU,Stock Qty,Low Stock Threshold,MRP (INR),Stock Value (INR),Status',
      ...products.map(p => {
        const st = p.stock_qty === 0 ? 'Out of Stock' : p.stock_qty <= (p.low_stock_threshold||5) ? 'Low Stock' : 'In Stock';
        return `"${p.name}","${p.category||''}","${p.brand||''}","${p.sku}",${p.stock_qty},${p.low_stock_threshold||5},${p.mrp||0},${(Number(p.stock_qty||0)*Number(p.mrp||0)).toFixed(2)},"${st}"`;
      }),
      '',
      'SUMMARY',
      `Total Products,${products.length}`,
      `In Stock,${inStock.length}`,
      `Low Stock,${lowStock.length}`,
      `Out of Stock,${outStock.length}`,
      `Total Inventory Value (MRP),${totalVal.toFixed(2)}`,
    ];
    saveCSV(`sakthi_solar_inventory_${todayStr()}.csv`, lines.join('\n'));
  };

  const doPDF = () => {
    const rows = products.map((p, i) => {
      const val = Number(p.stock_qty||0) * Number(p.mrp||0);
      const [stColor, stLabel] =
        p.stock_qty === 0 ? ['red','Out of Stock'] :
        p.stock_qty <= (p.low_stock_threshold||5) ? ['#b45309','Low Stock'] : ['green','In Stock'];
      return `<tr>
        <td>${i+1}</td><td><strong>${p.name}</strong></td><td>${p.category||'—'}</td><td>${p.brand||'—'}</td>
        <td>${p.sku}</td>
        <td style="text-align:center;font-weight:700;color:${stColor}">${p.stock_qty}</td>
        <td style="text-align:right">&#8377;${Number(p.mrp||0).toLocaleString('en-IN',{maximumFractionDigits:2})}</td>
        <td style="text-align:right;font-weight:600">&#8377;${val.toLocaleString('en-IN',{maximumFractionDigits:2})}</td>
        <td style="color:${stColor};font-weight:600">${stLabel}</td>
      </tr>`;
    }).join('');
    const body = `
      ${HDR('INVENTORY & STOCK REPORT', `As of ${new Date().toLocaleDateString('en-IN',{dateStyle:'medium'})}`)}
      <div class="sumgrid">
        <div class="sumbox" style="background:#f0f4ff;border:1px solid #bfdbfe"><div class="sumlbl">Total Products</div><div class="sumval" style="color:#1e3a5f">${products.length}</div></div>
        <div class="sumbox" style="background:#f0fdf4;border:1px solid #bbf7d0"><div class="sumlbl">In Stock</div><div class="sumval" style="color:green">${inStock.length}</div></div>
        <div class="sumbox" style="background:#fffbeb;border:1px solid #fde68a"><div class="sumlbl">Low Stock</div><div class="sumval" style="color:#b45309">${lowStock.length}</div></div>
        <div class="sumbox" style="background:#fef2f2;border:1px solid #fecaca"><div class="sumlbl">Out of Stock</div><div class="sumval" style="color:red">${outStock.length}</div></div>
      </div>
      <div style="background:#f0f4ff;border-left:3px solid #1e3a5f;padding:8px 12px;margin-bottom:12px;border-radius:4px;font-size:11px;color:#1e3a5f">
        <strong>Total Inventory Value (at MRP):</strong> &#8377;${totalVal.toLocaleString('en-IN',{maximumFractionDigits:2})}
      </div>
      <h2>All Products (${products.length})</h2>
      <table>
        <thead><tr><th>#</th><th>Product</th><th>Category</th><th>Brand</th><th>SKU</th><th style="text-align:center">Stock</th><th style="text-align:right">MRP</th><th style="text-align:right">Stock Value</th><th>Status</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="9" style="color:#aaa;text-align:center;padding:10px">No products found</td></tr>'}</tbody>
      </table>
    `;
    openPDF(body);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-hd"><div className="card-title"><Package s={15}/>Inventory &amp; Stock Report</div></div>
        <div className="card-body">
          {err && <div className="notice n-err" style={{ marginBottom: 14 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, color: 'var(--text2)', fontSize: 13 }}>Real-time snapshot of all products, stock levels, and inventory value.</div>
            <button className="btn btn-primary" onClick={generate} disabled={loading} style={{ whiteSpace: 'nowrap' }}>
              {loading ? 'Loading…' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {loading && <Spin label="Loading inventory…"/>}

      {generated && !loading && (
        <>
          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="stat-card" style={{ borderTop: '3px solid var(--navy)' }}>
              <div className="stat-lbl">Total Products</div>
              <div className="stat-val" style={{ fontSize: 22 }}>{products.length}</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--green)' }}>
              <div className="stat-lbl">In Stock</div>
              <div className="stat-val" style={{ color: 'var(--green)', fontSize: 22 }}>{inStock.length}</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--amber)' }}>
              <div className="stat-lbl">Low Stock</div>
              <div className="stat-val" style={{ color: 'var(--amber)', fontSize: 22 }}>{lowStock.length}</div>
              <div className="stat-sub">at or below threshold</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--red)' }}>
              <div className="stat-lbl">Out of Stock</div>
              <div className="stat-val" style={{ color: 'var(--red)', fontSize: 22 }}>{outStock.length}</div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--navy)', background: '#f0f4ff' }}>
            <div className="card-body">
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 4 }}>Total Inventory Value (at MRP)</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--navy)' }}>{fmt(totalVal)}</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>Current stock quantity × MRP per product</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={doPDF}><Download s={14}/> Download PDF</button>
            <button className="btn" onClick={doCSV}><Download s={14}/> Download CSV</button>
          </div>

          {lowStock.length > 0 && (
            <div className="card card-0" style={{ marginBottom: 16, borderLeft: '3px solid var(--amber)' }}>
              <div className="card-hd" style={{ background: '#fffbeb' }}>
                <div className="card-title" style={{ color: '#b45309' }}>Low Stock Alert — {lowStock.length} item{lowStock.length !== 1 ? 's' : ''}</div>
              </div>
              <div className="tbl-wrap"><table>
                <thead><tr><th>Product</th><th>Category</th><th>SKU</th><th>Stock Qty</th><th>Threshold</th><th>MRP</th></tr></thead>
                <tbody>
                  {lowStock.map(p => (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>{p.category}</td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.sku}</td>
                      <td><span className="badge b-warn">{p.stock_qty}</span></td>
                      <td>{p.low_stock_threshold || 5}</td>
                      <td>{fmt(p.mrp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}

          <div className="card card-0">
            <div className="card-hd"><div className="card-title">All Products ({products.length})</div></div>
            <div className="tbl-wrap"><table>
              <thead><tr><th>Product</th><th>Category</th><th>Brand</th><th>SKU</th><th>Stock</th><th>Threshold</th><th>MRP</th><th>Stock Value</th><th>Status</th></tr></thead>
              <tbody>
                {products.map(p => {
                  const val = Number(p.stock_qty || 0) * Number(p.mrp || 0);
                  const [cls, lbl] =
                    p.stock_qty === 0 ? ['b-err','Out of Stock'] :
                    p.stock_qty <= (p.low_stock_threshold||5) ? ['b-warn','Low Stock'] : ['b-ok','In Stock'];
                  return (
                    <tr key={p.id}>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>{p.category || '—'}</td>
                      <td>{p.brand || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--text2)' }}>{p.sku}</td>
                      <td style={{ fontWeight: 600 }}>{p.stock_qty}</td>
                      <td style={{ color: 'var(--text2)' }}>{p.low_stock_threshold || 5}</td>
                      <td>{fmt(p.mrp)}</td>
                      <td style={{ fontWeight: 600 }}>{fmt(val)}</td>
                      <td><span className={`badge ${cls}`}>{lbl}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Customer Analysis ────────────────────────────────────────────────────────

function CustomerReport() {
  const [from, setFrom]         = useState(daysAgo(30));
  const [to,   setTo]           = useState(todayStr());
  const [customers, setCust]    = useState([]);
  const [sales, setSales]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [generated, setGen]     = useState(false);
  const [err, setErr]           = useState('');

  const generate = async () => {
    if (from > to) { setErr('From date cannot be after To date.'); return; }
    setErr(''); setLoading(true);
    try {
      const [c, s] = await Promise.all([
        customersApi.list({ page_size: 2000 }),
        salesApi.list({ page_size: 2000 }),
      ]);
      setCust(c.data || []);
      setSales(s.data || []);
      setGen(true);
    } catch { setErr('Failed to load customer data.'); }
    finally { setLoading(false); }
  };

  const newCustomers  = customers.filter(c => c.created_at && c.created_at.slice(0,10) >= from && c.created_at.slice(0,10) <= to);
  const totalCust     = customers.length;

  const custSalesMap = {};
  sales.forEach(s => {
    if (!s.customer_id) return;
    if (!custSalesMap[s.customer_id]) custSalesMap[s.customer_id] = { paid: 0, pending: 0, count: 0 };
    if (s.payment_status === 'paid')    { custSalesMap[s.customer_id].paid    += Number(s.total); custSalesMap[s.customer_id].count++; }
    if (s.payment_status === 'partial') { custSalesMap[s.customer_id].paid    += Number(s.amount_paid||0); custSalesMap[s.customer_id].pending += Number(s.total) - Number(s.amount_paid||0); custSalesMap[s.customer_id].count++; }
    if (s.payment_status === 'unpaid')  { custSalesMap[s.customer_id].pending += Number(s.total); custSalesMap[s.customer_id].count++; }
  });

  const withPending   = customers.filter(c => (custSalesMap[c.id]?.pending || 0) > 0);
  const totalPending  = withPending.reduce((s, c) => s + (custSalesMap[c.id]?.pending || 0), 0);

  const topCustomers  = customers
    .filter(c => custSalesMap[c.id]?.paid > 0)
    .sort((a, b) => (custSalesMap[b.id]?.paid || 0) - (custSalesMap[a.id]?.paid || 0))
    .slice(0, 10);

  const doCSV = () => {
    const lines = [
      'Sakthi Solar Solutions - Customer Analysis Report',
      `Period: ${fmtDate(from)} to ${fmtDate(to)}`,
      '',
      'SUMMARY',
      `Total Customers,${totalCust}`,
      `New Customers (in period),${newCustomers.length}`,
      `Customers with Pending Balance,${withPending.length}`,
      `Total Pending Amount,${totalPending.toFixed(2)}`,
      '',
      'NEW CUSTOMERS IN PERIOD',
      'Name,Phone,District,Registered Date',
      ...newCustomers.map(c => `"${c.full_name}","${c.phone||''}","${c.district||''}","${c.created_at?.slice(0,10)||''}"`),
      '',
      'OUTSTANDING BALANCES',
      'Customer,Phone,Pending Amount (INR),No. of Sales',
      ...withPending.map(c => `"${c.full_name}","${c.phone||''}",${(custSalesMap[c.id]?.pending||0).toFixed(2)},${custSalesMap[c.id]?.count||0}`),
      '',
      'TOP CUSTOMERS BY REVENUE',
      'Customer,Phone,Total Paid (INR),No. of Sales',
      ...topCustomers.map(c => `"${c.full_name}","${c.phone||''}",${(custSalesMap[c.id]?.paid||0).toFixed(2)},${custSalesMap[c.id]?.count||0}`),
    ];
    saveCSV(`sakthi_solar_customers_${from}_to_${to}.csv`, lines.join('\n'));
  };

  const doPDF = () => {
    const newRows = newCustomers.map((c, i) => `<tr>
      <td>${i+1}</td><td><strong>${c.full_name}</strong></td><td>${c.phone||'—'}</td><td>${c.district||'—'}</td>
      <td>${c.created_at?.slice(0,10)||'—'}</td>
      <td style="text-align:right;font-weight:600">&#8377;${(custSalesMap[c.id]?.paid||0).toLocaleString('en-IN',{maximumFractionDigits:2})}</td>
    </tr>`).join('');
    const pendRows = withPending.slice(0,20).map((c, i) => `<tr>
      <td>${i+1}</td><td><strong>${c.full_name}</strong></td><td>${c.phone||'—'}</td>
      <td style="text-align:right;color:#b45309;font-weight:700">&#8377;${(custSalesMap[c.id]?.pending||0).toLocaleString('en-IN',{maximumFractionDigits:2})}</td>
      <td style="text-align:center">${custSalesMap[c.id]?.count||0}</td>
    </tr>`).join('');
    const topRows = topCustomers.map((c, i) => `<tr>
      <td>${i+1}</td><td><strong>${c.full_name}</strong></td><td>${c.phone||'—'}</td><td>${c.district||'—'}</td>
      <td style="text-align:right;color:green;font-weight:700">&#8377;${(custSalesMap[c.id]?.paid||0).toLocaleString('en-IN',{maximumFractionDigits:2})}</td>
      <td style="text-align:center">${custSalesMap[c.id]?.count||0}</td>
    </tr>`).join('');

    const body = `
      ${HDR('CUSTOMER ANALYSIS REPORT', `Period: ${fmtDate(from)} — ${fmtDate(to)}`)}
      <div class="sumgrid">
        <div class="sumbox" style="background:#f0f4ff;border:1px solid #bfdbfe"><div class="sumlbl">Total Customers</div><div class="sumval" style="color:#1e3a5f">${totalCust}</div></div>
        <div class="sumbox" style="background:#f0fdf4;border:1px solid #bbf7d0"><div class="sumlbl">New in Period</div><div class="sumval" style="color:green">${newCustomers.length}</div></div>
        <div class="sumbox" style="background:#fffbeb;border:1px solid #fde68a"><div class="sumlbl">With Pending Balance</div><div class="sumval" style="color:#b45309">${withPending.length}</div></div>
        <div class="sumbox" style="background:#fef2f2;border:1px solid #fecaca"><div class="sumlbl">Total Outstanding</div><div class="sumval" style="color:red;font-size:14px">&#8377;${totalPending.toLocaleString('en-IN',{maximumFractionDigits:2})}</div></div>
      </div>
      ${newCustomers.length > 0 ? `<h2>New Customers in Period (${newCustomers.length})</h2>
      <table><thead><tr><th>#</th><th>Name</th><th>Phone</th><th>District</th><th>Registered</th><th style="text-align:right">Revenue Paid</th></tr></thead>
      <tbody>${newRows}</tbody></table>` : ''}
      ${withPending.length > 0 ? `<h2>Outstanding Balances (${withPending.length} customers)</h2>
      <table><thead><tr><th>#</th><th>Customer</th><th>Phone</th><th style="text-align:right">Pending Amount</th><th style="text-align:center">Sales</th></tr></thead>
      <tbody>${pendRows}</tbody></table>` : ''}
      <h2>Top 10 Customers by Revenue</h2>
      <table><thead><tr><th>#</th><th>Customer</th><th>Phone</th><th>District</th><th style="text-align:right">Total Paid</th><th style="text-align:center">Sales</th></tr></thead>
      <tbody>${topRows||'<tr><td colspan="6" style="color:#aaa;text-align:center;padding:10px">No sales data</td></tr>'}</tbody></table>
    `;
    openPDF(body);
  };

  return (
    <div>
      <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} minDate="2020-01-01"
        onGo={generate} loading={loading}
        label={<><Users s={15}/>&nbsp;Customer Analysis Report</>}/>
      {err && <div className="notice n-err" style={{ marginBottom: 14 }}>{err}</div>}

      {loading && <Spin label="Loading customer data…"/>}

      {generated && !loading && (
        <>
          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="stat-card" style={{ borderTop: '3px solid var(--navy)' }}>
              <div className="stat-lbl">Total Customers</div>
              <div className="stat-val" style={{ fontSize: 22 }}>{totalCust}</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--green)' }}>
              <div className="stat-lbl">New in Period</div>
              <div className="stat-val" style={{ color: 'var(--green)', fontSize: 22 }}>{newCustomers.length}</div>
              <div className="stat-sub">{fmtDate(from)} – {fmtDate(to)}</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--amber)' }}>
              <div className="stat-lbl">With Pending Balance</div>
              <div className="stat-val" style={{ color: 'var(--amber)', fontSize: 22 }}>{withPending.length}</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--red)' }}>
              <div className="stat-lbl">Total Outstanding</div>
              <div className="stat-val" style={{ color: 'var(--red)', fontSize: 20 }}>{fmt(totalPending)}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={doPDF}><Download s={14}/> Download PDF</button>
            <button className="btn" onClick={doCSV}><Download s={14}/> Download CSV</button>
          </div>

          {newCustomers.length > 0 && (
            <div className="card card-0" style={{ marginBottom: 16 }}>
              <div className="card-hd"><div className="card-title">New Customers in Period ({newCustomers.length})</div></div>
              <div className="tbl-wrap"><table>
                <thead><tr><th>Name</th><th>Phone</th><th>District</th><th>Registered</th><th>Revenue (Paid)</th></tr></thead>
                <tbody>
                  {newCustomers.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.full_name}</td>
                      <td>{c.phone || '—'}</td>
                      <td>{c.district || '—'}</td>
                      <td style={{ fontSize: 12 }}>{c.created_at?.slice(0,10) || '—'}</td>
                      <td style={{ fontWeight: 600, color: 'var(--green)' }}>{fmt(custSalesMap[c.id]?.paid || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}

          {withPending.length > 0 && (
            <div className="card card-0" style={{ marginBottom: 16, borderLeft: '3px solid var(--amber)' }}>
              <div className="card-hd" style={{ background: '#fffbeb' }}>
                <div className="card-title" style={{ color: '#b45309' }}>Outstanding Balances ({withPending.length} customers)</div>
              </div>
              <div className="tbl-wrap"><table>
                <thead><tr><th>Customer</th><th>Phone</th><th>District</th><th>Pending Amount</th><th>No. of Sales</th></tr></thead>
                <tbody>
                  {withPending
                    .sort((a, b) => (custSalesMap[b.id]?.pending || 0) - (custSalesMap[a.id]?.pending || 0))
                    .map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.full_name}</td>
                      <td>{c.phone || '—'}</td>
                      <td>{c.district || '—'}</td>
                      <td style={{ fontWeight: 700, color: 'var(--amber)' }}>{fmt(custSalesMap[c.id]?.pending || 0)}</td>
                      <td>{custSalesMap[c.id]?.count || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}

          <div className="card card-0">
            <div className="card-hd"><div className="card-title">Top 10 Customers by Revenue</div></div>
            <div className="tbl-wrap"><table>
              <thead><tr><th>#</th><th>Customer</th><th>Phone</th><th>District</th><th>Total Paid</th><th>No. of Sales</th></tr></thead>
              <tbody>
                {topCustomers.length === 0
                  ? <tr><td colSpan={6} style={{ color: 'var(--text2)', textAlign: 'center', padding: 16 }}>No sales data available</td></tr>
                  : topCustomers.map((c, i) => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 700, color: 'var(--navy)' }}>{i + 1}</td>
                      <td style={{ fontWeight: 600 }}>{c.full_name}</td>
                      <td>{c.phone || '—'}</td>
                      <td>{c.district || '—'}</td>
                      <td style={{ fontWeight: 700, color: 'var(--green)' }}>{fmt(custSalesMap[c.id]?.paid || 0)}</td>
                      <td>{custSalesMap[c.id]?.count || 0}</td>
                    </tr>
                  ))
                }
              </tbody>
            </table></div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Service & Complaints ─────────────────────────────────────────────────────

function ServiceReport() {
  const [from, setFrom]         = useState(daysAgo(30));
  const [to,   setTo]           = useState(todayStr());
  const [complaints, setComplaints] = useState([]);
  const [services,   setServices]   = useState([]);
  const [enquiries,  setEnquiries]  = useState([]);
  const [loading, setLoading]       = useState(false);
  const [generated, setGen]         = useState(false);
  const [err, setErr]               = useState('');

  const generate = async () => {
    if (from > to) { setErr('From date cannot be after To date.'); return; }
    setErr(''); setLoading(true);
    try {
      const [c, s, e] = await Promise.all([
        complaintsApi.list({ page_size: 2000 }),
        servicesApi.list({ page_size: 2000 }),
        enquiriesApi.list({ page_size: 2000 }),
      ]);
      const inRange = ds => ds >= from && ds <= to;
      setComplaints((c.data || []).filter(x => inRange(x.created_at?.slice(0,10) || '')));
      setServices((s.data || []).filter(x => inRange(x.created_at?.slice(0,10) || '')));
      setEnquiries((e.data || []).filter(x => inRange(x.created_at?.slice(0,10) || '')));
      setGen(true);
    } catch { setErr('Failed to load service data.'); }
    finally { setLoading(false); }
  };

  const cOpen       = complaints.filter(c => c.status === 'open').length;
  const cProgress   = complaints.filter(c => c.status === 'in_progress').length;
  const cResolved   = complaints.filter(c => c.status === 'resolved').length;
  const cIncome     = complaints.reduce((s, c) => s + Number(c.amount_received || 0), 0);

  const sPending    = services.filter(s => s.status === 'pending').length;
  const sProgress   = services.filter(s => s.status === 'in_progress').length;
  const sCompleted  = services.filter(s => s.status === 'completed').length;

  const eNew        = enquiries.filter(e => e.status === 'new').length;
  const eContacted  = enquiries.filter(e => e.status === 'contacted').length;
  const eConverted  = enquiries.filter(e => e.status === 'converted').length;
  const eDropped    = enquiries.filter(e => e.status === 'dropped').length;

  const priorityMap = {};
  complaints.forEach(c => { priorityMap[c.priority || 'normal'] = (priorityMap[c.priority || 'normal'] || 0) + 1; });

  const doCSV = () => {
    const lines = [
      'Sakthi Solar Solutions - Service & Complaints Report',
      `Period: ${fmtDate(from)} to ${fmtDate(to)}`,
      '',
      'COMPLAINTS SUMMARY',
      `Total Complaints,${complaints.length}`,
      `Open,${cOpen}`,
      `In Progress,${cProgress}`,
      `Resolved,${cResolved}`,
      `Resolution Rate,${pct(cResolved, complaints.length)}`,
      `Service Income,${cIncome.toFixed(2)}`,
      '',
      'COMPLAINT DETAILS',
      'Code,Customer,Type,Priority,Status,Amount Received (INR),Created',
      ...complaints.map(c => `"${c.complaint_code||''}","${c.customer?.full_name||c.walk_in_name||''}","${c.complaint_type||''}","${c.priority||''}","${c.status}",${c.amount_received||0},"${c.created_at?.slice(0,10)||''}"`),
      '',
      'SERVICE REQUESTS SUMMARY',
      `Total,${services.length}`,
      `Pending,${sPending}`,
      `In Progress,${sProgress}`,
      `Completed,${sCompleted}`,
      `Completion Rate,${pct(sCompleted, services.length)}`,
      '',
      'SERVICE REQUEST DETAILS',
      'Code,Customer,Type,Status,Created',
      ...services.map(s => `"${s.service_code||''}","${s.customer?.full_name||''}","${s.service_type||''}","${s.status}","${s.created_at?.slice(0,10)||''}"`),
      '',
      'ENQUIRIES SUMMARY',
      `Total,${enquiries.length}`,
      `New,${eNew}`,
      `Contacted,${eContacted}`,
      `Converted,${eConverted}`,
      `Dropped,${eDropped}`,
      `Conversion Rate,${pct(eConverted, enquiries.length)}`,
    ];
    saveCSV(`sakthi_solar_service_${from}_to_${to}.csv`, lines.join('\n'));
  };

  const doPDF = () => {
    const compRows = complaints.slice(0,30).map((c, i) => `<tr>
      <td>${i+1}</td><td><strong>${c.complaint_code||'—'}</strong></td>
      <td>${c.customer?.full_name||c.walk_in_name||'—'}</td>
      <td>${c.complaint_type||'—'}</td>
      <td>${c.priority||'normal'}</td>
      <td style="color:${c.status==='resolved'?'green':c.status==='in_progress'?'#b45309':'#dc2626'}">${c.status}</td>
      <td style="text-align:right;font-weight:600">&#8377;${Number(c.amount_received||0).toLocaleString('en-IN',{maximumFractionDigits:2})}</td>
      <td>${c.created_at?.slice(0,10)||'—'}</td>
    </tr>`).join('');
    const svcRows = services.slice(0,20).map((s, i) => `<tr>
      <td>${i+1}</td><td><strong>${s.service_code||'—'}</strong></td>
      <td>${s.customer?.full_name||'—'}</td>
      <td>${s.service_type||'—'}</td>
      <td style="color:${s.status==='completed'?'green':s.status==='in_progress'?'#b45309':'#dc2626'}">${s.status}</td>
      <td>${s.created_at?.slice(0,10)||'—'}</td>
    </tr>`).join('');
    const enqRows = enquiries.slice(0,20).map((e, i) => `<tr>
      <td>${i+1}</td><td>${e.customer_name||'—'}</td><td>${e.product_interest||'—'}</td>
      <td>${e.source||'—'}</td>
      <td style="color:${e.status==='converted'?'green':e.status==='dropped'?'red':'#b45309'}">${e.status}</td>
      <td>${e.created_at?.slice(0,10)||'—'}</td>
    </tr>`).join('');

    const body = `
      ${HDR('SERVICE & COMPLAINTS REPORT', `Period: ${fmtDate(from)} — ${fmtDate(to)}`)}
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px;margin-bottom:14px">
        <div>
          <h2>Complaints (${complaints.length})</h2>
          <table style="width:auto">
            <tr><td>Open</td><td style="text-align:right;color:red;font-weight:700">${cOpen}</td></tr>
            <tr><td>In Progress</td><td style="text-align:right;color:#b45309;font-weight:700">${cProgress}</td></tr>
            <tr><td>Resolved</td><td style="text-align:right;color:green;font-weight:700">${cResolved}</td></tr>
            <tr><td>Resolution Rate</td><td style="text-align:right;font-weight:700">${pct(cResolved, complaints.length)}</td></tr>
            <tr><td>Service Income</td><td style="text-align:right;color:green;font-weight:700">&#8377;${cIncome.toLocaleString('en-IN',{maximumFractionDigits:2})}</td></tr>
          </table>
        </div>
        <div>
          <h2>Service Requests (${services.length})</h2>
          <table style="width:auto">
            <tr><td>Pending</td><td style="text-align:right;color:red;font-weight:700">${sPending}</td></tr>
            <tr><td>In Progress</td><td style="text-align:right;color:#b45309;font-weight:700">${sProgress}</td></tr>
            <tr><td>Completed</td><td style="text-align:right;color:green;font-weight:700">${sCompleted}</td></tr>
            <tr><td>Completion Rate</td><td style="text-align:right;font-weight:700">${pct(sCompleted, services.length)}</td></tr>
          </table>
        </div>
        <div>
          <h2>Enquiries (${enquiries.length})</h2>
          <table style="width:auto">
            <tr><td>New</td><td style="text-align:right;font-weight:700">${eNew}</td></tr>
            <tr><td>Contacted</td><td style="text-align:right;color:#b45309;font-weight:700">${eContacted}</td></tr>
            <tr><td>Converted</td><td style="text-align:right;color:green;font-weight:700">${eConverted}</td></tr>
            <tr><td>Dropped</td><td style="text-align:right;color:red;font-weight:700">${eDropped}</td></tr>
            <tr><td>Conversion Rate</td><td style="text-align:right;font-weight:700">${pct(eConverted, enquiries.length)}</td></tr>
          </table>
        </div>
      </div>
      ${complaints.length > 0 ? `<h2>Complaints Detail${complaints.length > 30 ? ' (top 30)' : ''}</h2>
      <table><thead><tr><th>#</th><th>Code</th><th>Customer</th><th>Type</th><th>Priority</th><th>Status</th><th style="text-align:right">Amount</th><th>Date</th></tr></thead>
      <tbody>${compRows}</tbody></table>` : ''}
      ${services.length > 0 ? `<h2>Service Requests${services.length > 20 ? ' (top 20)' : ''}</h2>
      <table><thead><tr><th>#</th><th>Code</th><th>Customer</th><th>Type</th><th>Status</th><th>Date</th></tr></thead>
      <tbody>${svcRows}</tbody></table>` : ''}
      ${enquiries.length > 0 ? `<h2>Enquiries${enquiries.length > 20 ? ' (top 20)' : ''}</h2>
      <table><thead><tr><th>#</th><th>Customer</th><th>Product</th><th>Source</th><th>Status</th><th>Date</th></tr></thead>
      <tbody>${enqRows}</tbody></table>` : ''}
    `;
    openPDF(body);
  };

  return (
    <div>
      <DateRange from={from} to={to} setFrom={setFrom} setTo={setTo} minDate="2020-01-01"
        onGo={generate} loading={loading}
        label={<><AlertCircle s={15}/>&nbsp;Service &amp; Complaints Report</>}/>
      {err && <div className="notice n-err" style={{ marginBottom: 14 }}>{err}</div>}

      {loading && <Spin label="Loading service data…"/>}

      {generated && !loading && (
        <>
          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="stat-card" style={{ borderTop: '3px solid var(--navy)' }}>
              <div className="stat-lbl">Total Complaints</div>
              <div className="stat-val" style={{ fontSize: 22 }}>{complaints.length}</div>
              <div className="stat-sub">{cOpen} open · {cProgress} in progress · {cResolved} resolved</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--green)' }}>
              <div className="stat-lbl">Resolution Rate</div>
              <div className="stat-val" style={{ color: 'var(--green)', fontSize: 22 }}>{pct(cResolved, complaints.length)}</div>
              <div className="stat-sub">{cResolved} of {complaints.length} resolved</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--amber)' }}>
              <div className="stat-lbl">Service Income</div>
              <div className="stat-val" style={{ color: 'var(--amber)', fontSize: 20 }}>{fmt(cIncome)}</div>
              <div className="stat-sub">from complaints</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--navy)' }}>
              <div className="stat-lbl">Enquiry Conversion</div>
              <div className="stat-val" style={{ fontSize: 22 }}>{pct(eConverted, enquiries.length)}</div>
              <div className="stat-sub">{eConverted} of {enquiries.length} converted</div>
            </div>
          </div>

          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 0 }}>
            <div className="stat-card" style={{ borderTop: '3px solid #6366f1' }}>
              <div className="stat-lbl">Service Requests</div>
              <div className="stat-val" style={{ fontSize: 22 }}>{services.length}</div>
              <div className="stat-sub">{sPending} pending · {sProgress} in prog · {sCompleted} done</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid #6366f1' }}>
              <div className="stat-lbl">Enquiries Received</div>
              <div className="stat-val" style={{ fontSize: 22 }}>{enquiries.length}</div>
              <div className="stat-sub">{eNew} new · {eContacted} contacted · {eDropped} dropped</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--red)' }}>
              <div className="stat-lbl">Open Issues</div>
              <div className="stat-val" style={{ color: 'var(--red)', fontSize: 22 }}>{cOpen + sPending}</div>
              <div className="stat-sub">{cOpen} complaints + {sPending} services</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, margin: '16px 0' }}>
            <button className="btn btn-primary" onClick={doPDF}><Download s={14}/> Download PDF</button>
            <button className="btn" onClick={doCSV}><Download s={14}/> Download CSV</button>
          </div>

          {complaints.length > 0 && (
            <div className="card card-0" style={{ marginBottom: 16 }}>
              <div className="card-hd"><div className="card-title">Complaints ({complaints.length})</div></div>
              <div className="tbl-wrap"><table>
                <thead><tr><th>Code</th><th>Customer</th><th>Type</th><th>Priority</th><th>Status</th><th>Amount Received</th><th>Date</th></tr></thead>
                <tbody>
                  {complaints.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600, fontSize: 12 }}>{c.complaint_code || '—'}</td>
                      <td>{c.customer?.full_name || c.walk_in_name || '—'}</td>
                      <td>{c.complaint_type || '—'}</td>
                      <td><span className={`badge ${c.priority === 'high' ? 'b-err' : c.priority === 'medium' ? 'b-warn' : 'b-info'}`}>{c.priority || 'normal'}</span></td>
                      <td><span className={`badge ${c.status === 'resolved' ? 'b-ok' : c.status === 'in_progress' ? 'b-warn' : 'b-err'}`}>{c.status}</span></td>
                      <td style={{ fontWeight: 600, color: 'var(--green)' }}>{Number(c.amount_received || 0) > 0 ? fmt(c.amount_received) : '—'}</td>
                      <td style={{ fontSize: 12 }}>{c.created_at?.slice(0,10) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}

          {services.length > 0 && (
            <div className="card card-0" style={{ marginBottom: 16 }}>
              <div className="card-hd"><div className="card-title">Service Requests ({services.length})</div></div>
              <div className="tbl-wrap"><table>
                <thead><tr><th>Code</th><th>Customer</th><th>Type</th><th>Status</th><th>Scheduled</th><th>Created</th></tr></thead>
                <tbody>
                  {services.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600, fontSize: 12 }}>{s.service_code || '—'}</td>
                      <td>{s.customer?.full_name || '—'}</td>
                      <td>{s.service_type || '—'}</td>
                      <td><span className={`badge ${s.status === 'completed' ? 'b-ok' : s.status === 'in_progress' ? 'b-warn' : 'b-err'}`}>{s.status}</span></td>
                      <td style={{ fontSize: 12 }}>{s.scheduled_date || '—'}</td>
                      <td style={{ fontSize: 12 }}>{s.created_at?.slice(0,10) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}

          {enquiries.length > 0 && (
            <div className="card card-0">
              <div className="card-hd"><div className="card-title">Enquiries ({enquiries.length})</div></div>
              <div className="tbl-wrap"><table>
                <thead><tr><th>Customer</th><th>Phone</th><th>Product Interest</th><th>Source</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {enquiries.map(e => (
                    <tr key={e.id}>
                      <td style={{ fontWeight: 600 }}>{e.customer_name || '—'}</td>
                      <td>{e.phone || '—'}</td>
                      <td>{e.product_interest || '—'}</td>
                      <td>{e.source || '—'}</td>
                      <td><span className={`badge ${e.status === 'converted' ? 'b-ok' : e.status === 'dropped' ? 'b-err' : e.status === 'contacted' ? 'b-warn' : 'b-info'}`}>{e.status}</span></td>
                      <td style={{ fontSize: 12 }}>{e.created_at?.slice(0,10) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Staff Attendance ─────────────────────────────────────────────────────────

function AttendanceReport() {
  const now = new Date();
  const [month, setMonth]       = useState(now.getMonth() + 1);
  const [year,  setYear]        = useState(now.getFullYear());
  const [records, setRecords]   = useState([]);
  const [staff,   setStaff]     = useState([]);
  const [loading, setLoading]   = useState(false);
  const [generated, setGen]     = useState(false);
  const [err, setErr]           = useState('');

  const YEARS = [];
  for (let y = now.getFullYear(); y >= 2023; y--) YEARS.push(y);
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const generate = async () => {
    setErr(''); setLoading(true);
    try {
      const [att, st] = await Promise.all([
        attendanceApi.list({ month, year }),
        staffApi.list(),
      ]);
      setRecords(att.data || []);
      setStaff(st.data || []);
      setGen(true);
    } catch { setErr('Failed to load attendance data.'); }
    finally { setLoading(false); }
  };

  const staffMap = {};
  staff.forEach(s => { staffMap[s.id] = s.name; });

  const byStaff = {};
  records.forEach(r => {
    if (!byStaff[r.staff_id]) byStaff[r.staff_id] = { name: r.staff_name || staffMap[r.staff_id] || r.staff_id, present: 0, absent: 0, leave: 0, half_day: 0, total: 0 };
    byStaff[r.staff_id].total++;
    if (r.status === 'present')  byStaff[r.staff_id].present++;
    if (r.status === 'absent')   byStaff[r.staff_id].absent++;
    if (r.status === 'leave')    byStaff[r.staff_id].leave++;
    if (r.status === 'half_day') byStaff[r.staff_id].half_day++;
  });

  const rows = Object.values(byStaff).sort((a, b) => a.name.localeCompare(b.name));
  const totalPresent = rows.reduce((s, r) => s + r.present, 0);
  const totalAbsent  = rows.reduce((s, r) => s + r.absent, 0);
  const totalDays    = rows.reduce((s, r) => s + r.total, 0);
  const overallPct   = totalDays > 0 ? ((totalPresent / totalDays) * 100).toFixed(1) + '%' : '—';

  const doCSV = () => {
    const lines = [
      'Sakthi Solar Solutions - Staff Attendance Report',
      `Month: ${MONTHS[month-1]} ${year}`,
      '',
      'Staff Name,Present,Absent,Leave,Half Day,Total Days Marked,Attendance %',
      ...rows.map(r => `"${r.name}",${r.present},${r.absent},${r.leave},${r.half_day},${r.total},${r.total > 0 ? ((r.present / r.total)*100).toFixed(1)+'%' : '—'}`),
      '',
      'SUMMARY',
      `Total Staff with Records,${rows.length}`,
      `Total Present Days,${totalPresent}`,
      `Total Absent Days,${totalAbsent}`,
      `Overall Attendance Rate,${overallPct}`,
    ];
    saveCSV(`sakthi_solar_attendance_${year}_${String(month).padStart(2,'0')}.csv`, lines.join('\n'));
  };

  const doPDF = () => {
    const tableRows = rows.map((r, i) => {
      const rate = r.total > 0 ? ((r.present / r.total) * 100).toFixed(1) : 0;
      const color = rate >= 90 ? 'green' : rate >= 75 ? '#b45309' : 'red';
      return `<tr>
        <td>${i+1}</td><td><strong>${r.name}</strong></td>
        <td style="text-align:center;color:green;font-weight:600">${r.present}</td>
        <td style="text-align:center;color:red;font-weight:600">${r.absent}</td>
        <td style="text-align:center;color:#6366f1;font-weight:600">${r.leave}</td>
        <td style="text-align:center;font-weight:600">${r.half_day}</td>
        <td style="text-align:center">${r.total}</td>
        <td style="text-align:center;color:${color};font-weight:700">${r.total > 0 ? rate + '%' : '—'}</td>
      </tr>`;
    }).join('');

    const body = `
      ${HDR('STAFF ATTENDANCE REPORT', `${MONTHS[month-1]} ${year}`)}
      <div class="sumgrid">
        <div class="sumbox" style="background:#f0f4ff;border:1px solid #bfdbfe"><div class="sumlbl">Staff with Records</div><div class="sumval" style="color:#1e3a5f">${rows.length}</div></div>
        <div class="sumbox" style="background:#f0fdf4;border:1px solid #bbf7d0"><div class="sumlbl">Total Present Days</div><div class="sumval" style="color:green">${totalPresent}</div></div>
        <div class="sumbox" style="background:#fef2f2;border:1px solid #fecaca"><div class="sumlbl">Total Absent Days</div><div class="sumval" style="color:red">${totalAbsent}</div></div>
        <div class="sumbox" style="background:${parseFloat(overallPct)>=80?'#f0fdf4':'#fffbeb'};border:1px solid ${parseFloat(overallPct)>=80?'#bbf7d0':'#fde68a'}"><div class="sumlbl">Overall Attendance</div><div class="sumval" style="color:${parseFloat(overallPct)>=80?'green':'#b45309'}">${overallPct}</div></div>
      </div>
      <h2>Staff-wise Breakdown</h2>
      <table>
        <thead><tr><th>#</th><th>Staff Name</th><th style="text-align:center">Present</th><th style="text-align:center">Absent</th><th style="text-align:center">Leave</th><th style="text-align:center">Half Day</th><th style="text-align:center">Days Marked</th><th style="text-align:center">Attendance %</th></tr></thead>
        <tbody>${tableRows || '<tr><td colspan="8" style="color:#aaa;text-align:center;padding:10px">No attendance records found</td></tr>'}</tbody>
      </table>
    `;
    openPDF(body);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-hd"><div className="card-title"><Calendar s={15}/>Staff Attendance Report</div></div>
        <div className="card-body">
          {err && <div className="notice n-err" style={{ marginBottom: 14 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>Month</label>
              <select value={month} onChange={e => setMonth(Number(e.target.value))}>
                {MONTHS.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', display: 'block', marginBottom: 5 }}>Year</label>
              <select value={year} onChange={e => setYear(Number(e.target.value))}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" onClick={generate} disabled={loading} style={{ whiteSpace: 'nowrap' }}>
              {loading ? 'Loading…' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {loading && <Spin label="Loading attendance data…"/>}

      {generated && !loading && (
        <>
          <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
            <div className="stat-card" style={{ borderTop: '3px solid var(--navy)' }}>
              <div className="stat-lbl">Staff with Records</div>
              <div className="stat-val" style={{ fontSize: 22 }}>{rows.length}</div>
              <div className="stat-sub">for {MONTHS[month-1]} {year}</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--green)' }}>
              <div className="stat-lbl">Total Present Days</div>
              <div className="stat-val" style={{ color: 'var(--green)', fontSize: 22 }}>{totalPresent}</div>
            </div>
            <div className="stat-card" style={{ borderTop: '3px solid var(--red)' }}>
              <div className="stat-lbl">Total Absent Days</div>
              <div className="stat-val" style={{ color: 'var(--red)', fontSize: 22 }}>{totalAbsent}</div>
            </div>
            <div className="stat-card" style={{ borderTop: `3px solid ${parseFloat(overallPct) >= 80 ? 'var(--green)' : 'var(--amber)'}` }}>
              <div className="stat-lbl">Overall Attendance</div>
              <div className="stat-val" style={{ color: parseFloat(overallPct) >= 80 ? 'var(--green)' : 'var(--amber)', fontSize: 22 }}>{overallPct}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={doPDF}><Download s={14}/> Download PDF</button>
            <button className="btn" onClick={doCSV}><Download s={14}/> Download CSV</button>
          </div>

          <div className="card card-0">
            <div className="card-hd"><div className="card-title">Staff-wise Attendance — {MONTHS[month-1]} {year}</div></div>
            <div className="tbl-wrap"><table>
              <thead><tr><th>Staff Name</th><th style={{ textAlign: 'center' }}>Present</th><th style={{ textAlign: 'center' }}>Absent</th><th style={{ textAlign: 'center' }}>Leave</th><th style={{ textAlign: 'center' }}>Half Day</th><th style={{ textAlign: 'center' }}>Days Marked</th><th style={{ textAlign: 'center' }}>Attendance %</th></tr></thead>
              <tbody>
                {rows.length === 0
                  ? <tr><td colSpan={7} style={{ color: 'var(--text2)', textAlign: 'center', padding: 20 }}>No attendance records for {MONTHS[month-1]} {year}</td></tr>
                  : rows.map(r => {
                    const rate = r.total > 0 ? ((r.present / r.total) * 100).toFixed(1) : null;
                    const color = rate === null ? 'var(--text2)' : parseFloat(rate) >= 90 ? 'var(--green)' : parseFloat(rate) >= 75 ? 'var(--amber)' : 'var(--red)';
                    return (
                      <tr key={r.name}>
                        <td style={{ fontWeight: 600 }}>{r.name}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--green)' }}>{r.present}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--red)' }}>{r.absent}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600, color: '#6366f1' }}>{r.leave}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{r.half_day}</td>
                        <td style={{ textAlign: 'center', color: 'var(--text2)' }}>{r.total}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color }}>
                          {rate !== null ? rate + '%' : '—'}
                        </td>
                      </tr>
                    );
                  })
                }
              </tbody>
            </table></div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main Reports Page ────────────────────────────────────────────────────────

const TABS = [
  { id: 'financial',  label: 'Financial',             Icon: BarChart2 },
  { id: 'inventory',  label: 'Inventory & Stock',      Icon: Package,  saOnly: true },
  { id: 'customers',  label: 'Customer Analysis',      Icon: Users,    saOnly: true },
  { id: 'service',    label: 'Service & Complaints',   Icon: AlertCircle, saOnly: true },
  { id: 'attendance', label: 'Staff Attendance',       Icon: Calendar, saOnly: true },
];

export default function Reports() {
  const { isSA } = useAuth();
  const [tab, setTab] = useState('financial');

  const visibleTabs = TABS.filter(t => !t.saOnly || isSA);

  return (
    <div>
      {isSA && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {visibleTabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className="btn"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: tab === t.id ? 'var(--navy)' : undefined,
                color: tab === t.id ? '#fff' : undefined,
                borderColor: tab === t.id ? 'var(--navy)' : undefined,
                fontWeight: tab === t.id ? 700 : undefined,
              }}
            >
              <t.Icon s={13}/> {t.label}
            </button>
          ))}
        </div>
      )}

      {tab === 'financial'  && <FinancialReport isSA={isSA}/>}
      {tab === 'inventory'  && isSA && <InventoryReport/>}
      {tab === 'customers'  && isSA && <CustomerReport/>}
      {tab === 'service'    && isSA && <ServiceReport/>}
      {tab === 'attendance' && isSA && <AttendanceReport/>}
    </div>
  );
}
